import * as Immutable from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { hideMessage } from '../../actions/AuthenticationActions';
import { getCompetitionsLevels } from '../../actions/CompetitionsActions';
import { getUserAchievements } from '../../actions/Profile/AchievementsActions';
import { DashboardMain } from '../../components';
import { StateType } from '../../reducers';
import { accountDataFieldsCountrySelector } from '../../reducers/AccountReducer/AccountSelector';
import {
  getUserFirstNameSelector,
  getUserIdSelector,
  getUserOnboardingSelector,
  getUserPermissionsSelector,
  getUserShowMessageSelector,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { getConsultantSelector } from '../../reducers/ConsultantReducer/ConsultantSelector';
import {
  dashboardCompetitionsLevelsOptionsSelector,
  dashboardLatestCompetitionsDataSelector,
} from '../../reducers/DashboardReducer/CompetitionsReducer/CompetitionsSelector';
import { getLocalStorageItem } from '../../shared/helpers/local-storage';
import { DispatchConnect, OnboardingStatus, Permission } from '../../types/';
import { ErrorBoundary } from '../ErrorBoundary';

import DashboardIntro from './DashboardIntro';

interface DashboardState {
  dashboardSections: any[];
  enabledIntro: boolean;
}

interface DashboardStateProps {
  country: string;
  dataCompetitions: Immutable.List<any>;
  dataCompetitionsLevels: Immutable.Map<string, string>;
  showMessage: boolean;
  username: string;
  userId: string;
  userPermissions: Immutable.List<Permission>;
  userOnboarding: Immutable.Map<string, string> | null;
  userConsultant: Immutable.Map<string, string>;
}

interface DashboardStateDispatchers {
  dispatchHideMessage: () => void;
  dispatchGetUserAchievements: (userId: string) => void;
  dispatchGetCompetitionsLevels: () => void;
}

type DashboardPropsGeneric = DashboardStateProps & DashboardStateDispatchers;

// TODO: Move to environment variable
const FINISHED_ONBOARDING_DISPLAY_PERIOD = 7 * 24 * 60 * 60 * 1000; // a week

const shouldShowOnboarding = (props: DashboardPropsGeneric): boolean => {
  if (!props.userOnboarding) {
    return false;
  }

  const status = props.userOnboarding.getIn(['status']) as OnboardingStatus;
  if (status === 'NONE') {
    return false;
  }

  if (status === 'CONSULTANT_APPROVED') {
    const isFetchConsultantFailure = props.userConsultant.getIn(['failure']);
    if (isFetchConsultantFailure) {
      return false;
    }

    const now = new Date().getTime();
    // @ts-expect-error ImmutableJS getIn returns unknown
    const consultantFrom = new Date(props.userConsultant.getIn(['data', 'dateStarted'])).getTime();

    return now - consultantFrom < FINISHED_ONBOARDING_DISPLAY_PERIOD;
  }

  return true;
};

class Dashboard extends React.Component<DashboardPropsGeneric, DashboardState> {
  constructor(props: DashboardPropsGeneric) {
    super(props);

    this.state = {
      dashboardSections: ['genius', 'events', 'announcements', 'notification', 'consultant', 'competitions', 'alphas', 'achievements'],
      enabledIntro: false,
    };
  }

  componentDidMount() {
    const newState = { ...this.state };

    const isPaidUser = this.props.userPermissions.includes('CONSULTANT') || this.props.userPermissions.includes('RESEARCHER');
    const walkthrough = isPaidUser ? 'dashboardWalkthrough-consultant' : 'dashboardWalkthrough';
    const dashboardWalkthrough: any = getLocalStorageItem(this.props.userId, walkthrough);
    if (!dashboardWalkthrough && this.props.userId) {
      this.props.dispatchGetUserAchievements(this.props.userId);
      newState.enabledIntro = true;
    }

    const isOnboardingShown = shouldShowOnboarding(this.props);
    if (isOnboardingShown) {
      newState.dashboardSections = ['onboarding', ...newState.dashboardSections];
    }
    newState.dashboardSections = newState.dashboardSections.filter(
      section => isPaidUser || (section !== 'consultant' && section !== 'alphas' && section !== 'genius')
    );

    this.props.dispatchGetCompetitionsLevels();
    this.setState(newState);
  }

  UNSAFE_componentWillReceiveProps(nextProps: DashboardPropsGeneric): void {
    const { userId } = this.props;

    if (nextProps.userId && nextProps.userId !== userId) {
      const walkthrough =
        nextProps.userPermissions.includes('CONSULTANT') || nextProps.userPermissions.includes('RESEARCHER')
          ? 'dashboardWalkthrough-consultant'
          : 'dashboardWalkthrough';
      const dashboardWalkthrough: any = getLocalStorageItem(nextProps.userId, walkthrough);
      if (!dashboardWalkthrough && nextProps.userId) {
        this.props.dispatchGetUserAchievements(nextProps.userId);
        this.setState({ enabledIntro: true });
      }
    }
  }

  handleOnExitIntro = () => {
    this.setState({ enabledIntro: false });
  };

  renderIntro() {
    if (this.state.enabledIntro) {
      // @ts-expect-error connect own-props collapse under @types/react@19
      return <DashboardIntro handleOnExitIntro={this.handleOnExitIntro} />;
    } else {
      return null;
    }
  }

  render() {
    return (
      <ErrorBoundary showUI={true} isNotFullScreen={true}>
        <div>
          <DashboardMain
            country={this.props.country}
            dataCompetitions={this.props.dataCompetitions}
            dataCompetitionsLevels={this.props.dataCompetitionsLevels}
            userOnboardingData={this.props.userOnboarding}
            dashboardSections={this.state.dashboardSections}
            enabledIntro={this.state.enabledIntro}
          />
          {this.renderIntro()}
        </div>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state: StateType): DashboardStateProps => ({
  // @ts-expect-error connect own-props collapse under @types/react@19
  country: accountDataFieldsCountrySelector(state),
  dataCompetitions: dashboardLatestCompetitionsDataSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  dataCompetitionsLevels: dashboardCompetitionsLevelsOptionsSelector(state),
  // @ts-expect-error getUserFirstNameSelector returns string | undefined; prop expects string
  username: getUserFirstNameSelector(state),
  // @ts-expect-error getUserIdSelector returns string | undefined; prop expects string
  userId: getUserIdSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  showMessage: getUserShowMessageSelector(state),
  userPermissions: getUserPermissionsSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  userOnboarding: getUserOnboardingSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  userConsultant: getConsultantSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DashboardStateDispatchers => ({
  dispatchHideMessage: () => {
    dispatch(hideMessage());
  },
  dispatchGetUserAchievements: async (userId: string) => dispatch(getUserAchievements(userId)),
  dispatchGetCompetitionsLevels: async () => dispatch(getCompetitionsLevels()),
});

export default connect<DashboardStateProps, DispatchConnect<DashboardStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);
